import { z } from 'zod';

import type { AsContract, MatchesSchema } from '@/shared/api/contract';

/**
 * Import dwufazowy (decyzje 98–108) — <b>przygotowany wsad, który człowiek ogląda przed zapisem</b>.
 *
 * ⭐ Dwa etapy dla człowieka: **analiza** („1 826 wejdzie bez pytania" plus lista konfliktów)
 * i **obróbka z zatwierdzeniem** — można wrócić nazajutrz. Trzy fazy techniczne siedzą po
 * stronie serwera i front ich nie widzi.
 */

/** ⭐ Cztery stany, nie pięć: „analiza" jest OPERACJĄ, nie miejscem, w którym przebieg spoczywa. */
export const importRunStateSchema = z.enum(['open', 'committed', 'rolledBack', 'abandoned']);

export type ImportRunState = z.infer<typeof importRunStateSchema>;

/**
 * Rodzaje konfliktu (decyzja 99 + konflikty właściciela z 2026-09-04).
 *
 * ⭐ <b>Kolejność JEST kolejnością ciężkości</b>, od najcięższego, i mierzy ją
 * <b>nieodwracalność złej decyzji</b>: błędne sklejenie dwóch bytów jest „prawie nie do
 * rozdzielenia", przeniesienie da się cofnąć, różnicę wartości poprawia się w GUI.
 *
 * ⚠ `disabledOwner` stoi wyżej niż `inactiveOwner`, bo tylko w nim plik próbuje unieważnić
 * **świadomą decyzję człowieka**. ⛔ Kolejność musi zgadzać się z backendem co do NUMERU —
 * „rodzaj główny" pozycji to tam `MIN(kind)`.
 *
 * ⚠ `unknownOwner` zniknął: byt, którego nie ma, nie jest już pytaniem — zatwierdzenie go
 * zakłada, bo plik niesie i tożsamość, i nazwę.
 */
export const importConflictKindSchema = z.enum([
  'duplicateSuspect',
  'reparent',
  'deletedReturns',
  'valueDiffers',
  'disabledOwner',
  'inactiveOwner',
  'unknownType',
  'danglingRef',
]);

export type ImportConflictKind = z.infer<typeof importConflictKindSchema>;

export const importConflictStateSchema = z.enum(['waiting', 'resolved', 'skipped']);

export type ImportConflictState = z.infer<typeof importConflictStateSchema>;

// ── Nagłówek przebiegu ───────────────────────────────────────────────────────────

/**
 * Opcje przebiegu — obie domyślnie **wyłączone**.
 *
 * ⚠ `assignInactiveOwners` NIE dotyczy kont zablokowanych: te dają konflikt zawsze, bo plik
 * nie unieważnia decyzji admina.
 */
export const importRunOptionsSchema = z.object({
  overwriteDisplayNames: z.boolean(),
  assignInactiveOwners: z.boolean(),
});

export type ImportRunOptions = AsContract<z.infer<typeof importRunOptionsSchema>>;

export const importRunSummarySchema = z.object({
  id: z.number(),
  source: z.string(),
  state: importRunStateSchema,
  fileName: z.string().nullable(),
  fileVersion: z.number(),
  startedAtUtc: z.string(),
  startedByUserId: z.number(),
  analysisStartedAtUtc: z.string().nullable(),
  analyzedAtUtc: z.string().nullable(),
  // Stempel padu analizy w tle (dec. 136) — bez niego pad workera wyglądał jak
  // wieczne „analiza trwa" i nie było go jak pokazać.
  analysisFailedAtUtc: z.string().nullable(),
  committedAtUtc: z.string().nullable(),
  rolledBackAtUtc: z.string().nullable(),
  rejectionReasonKey: z.string().nullable(),
  /**
   * Opcje przebiegu (2026-09-04) — **edytowalne do zatwierdzenia**: przełącznik zmienia liczby,
   * na które człowiek właśnie patrzy, więc decyzja przy wgrywaniu byłaby decyzją podjętą, zanim
   * cokolwiek zobaczył.
   */
  options: importRunOptionsSchema,
  /** Ile kont założył ten przebieg. ⛔ Liczba, na której stoi decyzja o zatwierdzeniu. */
  usersCreated: z.number(),
  teamsCreated: z.number(),
  displayNamesUpdated: z.number(),
  analysisInProgress: z.boolean().optional(),
  analysisFailed: z.boolean().optional(),
});

export type ImportRunSummary = AsContract<z.infer<typeof importRunSummarySchema>>;

// ── Liczby ───────────────────────────────────────────────────────────────────────

/**
 * ⭐ „Nie wejdzie, bo <b>pominięte</b>" i „nie wejdzie, bo <b>nierozstrzygnięte</b>" to dwie
 * różne liczby i nie wolno ich skleić: pierwsze jest decyzją i nie blokuje, drugie blokuje
 * zatwierdzenie. Jedna wspólna zrobiłaby z zatwierdzania zgadywankę.
 */
export const importCountsSchema = z.object({
  fileElements: z.number(),
  unchanged: z.number(),
  newElements: z.number(),
  updatedElements: z.number(),
  adoptedKeys: z.number(),
  relations: z.number(),
  danglingRelations: z.number(),
  owners: z.number(),
  /**
   * Ile kont i zespołów zatwierdzenie **założy** (2026-09-04).
   *
   * ⭐ Zapowiedź PRZED zapisem, bo konta z importu są nieodwracalne — wycofanie przebiegu
   * ich nie usuwa.
   */
  newUsers: z.number(),
  newTeams: z.number(),
  /**
   * Ile istniejących kont i zespołów ma w pliku **inną** nazwę wyświetlaną.
   *
   * ⚠ Liczona NIEZALEŻNIE od opcji „nadpisuj nazwy" i dlatego uczciwa w obu stanach
   * przełącznika: mówi „tyle nazw ta opcja by zmieniła", a nie „tyle zmieni".
   */
  displayNamesDiffer: z.number(),
  conflictsWaiting: z.number(),
  conflictsResolved: z.number(),
  conflictsSkipped: z.number(),
  canCommit: z.boolean().optional(),
});

export type ImportCounts = AsContract<z.infer<typeof importCountsSchema>>;

export const importConflictGroupSchema = z.object({
  kind: importConflictKindSchema,
  total: z.number(),
  resolved: z.number(),
  skipped: z.number(),
});

export type ImportConflictGroup = AsContract<z.infer<typeof importConflictGroupSchema>>;

/**
 * ⭐ Ile zobowiązań <b>dosypią kampanie</b> po zatwierdzeniu (R27). Liczba jest prawdziwa —
 * liczona selektorem na wirtualnym grafie. Wsypanie ośmiu tysięcy serwisów przy kampanii
 * „każdy serwis ma właściciela" <b>utworzy pracę ludziom, którzy o imporcie nie wiedzą</b>.
 */
export const importCampaignImpactSchema = z.object({
  campaignId: z.number(),
  title: z.string(),
  commitments: z.number(),
});

export type ImportCampaignImpact = AsContract<z.infer<typeof importCampaignImpactSchema>>;

/**
 * Kontyngent elementów tenanta wobec tego przebiegu (dec. 154): limit efektywny, stan grafu
 * teraz i stan PO zatwierdzeniu (teraz + nowe, liczone tym samym warunkiem, co commit).
 * ⭐ Ekran blokuje „Zatwierdź" z powodem, zanim serwer odmówi — a serwer i tak odmówi pod
 * blokadą, gdyby ktoś w międzyczasie dołożył elementy ręcznie.
 */
export const importElementsQuotaSchema = z.object({
  max: z.number(),
  current: z.number(),
  afterCommit: z.number(),
  wouldExceed: z.boolean().optional(),
});

export type ImportElementsQuota = AsContract<z.infer<typeof importElementsQuotaSchema>>;

export const importAnalysisSchema = z.object({
  run: importRunSummarySchema,
  counts: importCountsSchema,
  groups: z.array(importConflictGroupSchema),
  campaigns: z.array(importCampaignImpactSchema),
  elementsQuota: importElementsQuotaSchema,
});

export type ImportAnalysis = AsContract<z.infer<typeof importAnalysisSchema>>;

// ── Ładunki rodzajów ─────────────────────────────────────────────────────────────

export const duplicateSuspectPayloadSchema = z.object({
  candidateElementId: z.number(),
  candidateTitle: z.string(),
  candidateLastTouchedByUserId: z.number().nullable(),
  candidateUpdatedAtUtc: z.string().nullable(),
});

export type DuplicateSuspectPayload = AsContract<z.infer<typeof duplicateSuspectPayloadSchema>>;

export const reparentPayloadSchema = z.object({
  currentParentId: z.number().nullable(),
  currentParentTitle: z.string().nullable(),
  fileParentSource: z.string().nullable(),
  fileParentKey: z.string().nullable(),
  fileParentId: z.number().nullable(),
  fileParentTitle: z.string().nullable(),
  subtreeCount: z.number(),
  currentParentBecomesEmpty: z.boolean(),
});

export type ReparentPayload = AsContract<z.infer<typeof reparentPayloadSchema>>;

export const deletedReturnsPayloadSchema = z.object({
  deletedAtUtc: z.string(),
  relationCount: z.number(),
});

export type DeletedReturnsPayload = AsContract<z.infer<typeof deletedReturnsPayloadSchema>>;

/** ⭐ `caseOnly` — różnica <b>wyłącznie w wielkości liter</b>, żeby dała się rozwiązać hurtem (R5). */
export const importFieldDiffSchema = z.object({
  field: z.string(),
  dbValue: z.string().nullable(),
  fileValue: z.string().nullable(),
  caseOnly: z.boolean(),
});

export type ImportFieldDiff = AsContract<z.infer<typeof importFieldDiffSchema>>;

export const valueDiffersPayloadSchema = z.object({
  fields: z.array(importFieldDiffSchema),
  lastTouchedByUserId: z.number().nullable(),
  lastTouchedImportRunId: z.number().nullable(),
  updatedAtUtc: z.string(),
});

export type ValueDiffersPayload = AsContract<z.infer<typeof valueDiffersPayloadSchema>>;

/** Ten sam kształt dla <b>typu</b> i <b>właściciela</b> — oba to jedna pomyłka generatora powtórzona setki razy. */
export const unknownValuePayloadSchema = z.object({
  fileValue: z.string(),
  occurrences: z.number(),
});

export type UnknownValuePayload = AsContract<z.infer<typeof unknownValuePayloadSchema>>;

export const danglingRefPayloadSchema = z.object({
  missingSource: z.string(),
  missingKey: z.string(),
  isParent: z.boolean(),
});

export type DanglingRefPayload = AsContract<z.infer<typeof danglingRefPayloadSchema>>;

export const importConflictDetailSchema = z.object({
  kind: importConflictKindSchema,
  /**
   * Wybór zapisany dla TEGO rodzaju — `null`, gdy jeszcze go nie ma.
   *
   * ⭐ Bez tego ekran nie umiał pokazać decyzji CZĄSTKOWEJ, a pozycja z dwoma rodzajami jest
   * normą. Objaw z żywego ekranu: kliknięcie wyboru zwijało wiersz i nie zmieniało niczego,
   * bo stan całej pozycji zostaje „czeka", dopóki choć jeden rodzaj jest nierozstrzygnięty.
   */
  choice: z.string().nullable().optional(),
  duplicateSuspect: duplicateSuspectPayloadSchema.nullable().optional(),
  reparent: reparentPayloadSchema.nullable().optional(),
  deletedReturns: deletedReturnsPayloadSchema.nullable().optional(),
  valueDiffers: valueDiffersPayloadSchema.nullable().optional(),
  unknownValue: unknownValuePayloadSchema.nullable().optional(),
  danglingRef: danglingRefPayloadSchema.nullable().optional(),
});

export type ImportConflictDetail = AsContract<z.infer<typeof importConflictDetailSchema>>;

/** ⭐ <b>Jedna pozycja na element</b>, nawet gdy niesie kilka rodzajów — wtedy kilka plakietek. */
export const importConflictItemSchema = z.object({
  id: z.number(),
  externalKey: z.string(),
  fileTitle: z.string().nullable(),
  elementId: z.number().nullable(),
  dbTitle: z.string().nullable(),
  primaryKind: importConflictKindSchema,
  /**
   * Dlaczego rozstrzygnięta pozycja WRÓCIŁA do „czeka" (`file_changed` / `db_changed`).
   *
   * ⭐ Bez tego wracająca pozycja wygląda jak utracona praca: wybór zostaje zaznaczony,
   * stan wraca na „czeka", i nie ma jak się dowiedzieć, że zmieniła się podstawa decyzji.
   */
  staleReasonKey: z.string().nullable(),
  state: importConflictStateSchema,
  kinds: z.array(importConflictDetailSchema),
});

export type ImportConflictItem = AsContract<z.infer<typeof importConflictItemSchema>>;

// ── Strony i wyniki ──────────────────────────────────────────────────────────────

export const importIntakeResultSchema = z.object({
  runId: z.number(),
  source: z.string(),
  fileVersion: z.number(),
  elements: z.number(),
  relations: z.number(),
  owners: z.number(),
  sourceIsNew: z.boolean(),
});

export type ImportIntakeResult = AsContract<z.infer<typeof importIntakeResultSchema>>;

export const importResolveResultSchema = z.object({
  conflictId: z.number(),
  choice: z.string(),
  affected: z.number(),
});

export type ImportResolveResult = AsContract<z.infer<typeof importResolveResultSchema>>;

/**
 * ⚠ `kind` jest **wyliczeniem**, nie dowolnym tekstem. Wcześniej szło po drucie jako
 * `PascalCase` z `ToString()` po stronie serwera, więc ekran nie trafiał kluczem tłumaczenia
 * i wypisywał **surową nazwę wartości enuma**. `z.enum` odrzuca nieznaną wartość NA GRANICY.
 */
export const importChangeKindSchema = z.enum([
  'elementAdded',
  'elementFieldUpdated',
  'relationAdded',
  'relationSkipped',
  'keyAdopted',
  'ownerAdded',
  'ownerReadded',
]);

export const importChangeSchema = z.object({
  kind: importChangeKindSchema,
  externalKey: z.string(),
  elementId: z.number().nullable(),
  field: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  reasonKey: z.string().nullable(),
  /** Tytuł drugiego końca krawędzi — bez niego wiersz „nowa relacja" pokazywał gołe id. */
  relatedTitle: z.string().nullable(),
});

export type ImportChange = AsContract<z.infer<typeof importChangeSchema>>;

export const importConflictPageSchema = z.object({
  items: z.array(importConflictItemSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type ImportConflictPage = AsContract<z.infer<typeof importConflictPageSchema>>;

export const importChangePageSchema = z.object({
  items: z.array(importChangeSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type ImportChangePage = AsContract<z.infer<typeof importChangePageSchema>>;

// ── Wycofanie ────────────────────────────────────────────────────────────────────

/**
 * ⛔ `canForce === false` przy zobowiązaniu i przy adopcji klucza. Pierwsze, bo usunięcie
 * elementu z zamkniętym zobowiązaniem kasuje <b>dowód, że ktoś wykonał pracę</b>; drugie,
 * bo import tego elementu <b>nie stworzył</b>.
 */
export const importRollbackBlockerSchema = z.object({
  reasonKey: z.string(),
  count: z.number(),
  canForce: z.boolean(),
});

export type ImportRollbackBlocker = AsContract<z.infer<typeof importRollbackBlockerSchema>>;

export const importRollbackPreviewSchema = z.object({
  runId: z.number(),
  elementsToDelete: z.number(),
  valuesToRestore: z.number(),
  relationsToRemove: z.number(),
  keysToRelease: z.number(),
  blockers: z.array(importRollbackBlockerSchema),
  adoptedKeys: z.number(),
  valuesChangedAfterImport: z.number(),
  kept: z.number(),
  /**
   * Właściciele, których **przyniósł ten przebieg** i którzy zostaną zdjęci.
   *
   * ⭐ Decyzja usera 2026-08-08: „tego właściciela przyniósł import i jeżeli wycofujemy import,
   * należy wycofać jak najwięcej tego, co przyniósł". ⚠ Liczy wyłącznie wiersze dopisane przez
   * ten przebieg — właściciel przypięty ręką po imporcie zostaje i dalej blokuje usunięcie.
   */
  ownersToRemove: z.number(),
});

export type ImportRollbackPreview = AsContract<z.infer<typeof importRollbackPreviewSchema>>;

export const importRollbackResultSchema = z.object({
  runId: z.number(),
  elementsDeleted: z.number(),
  valuesRestored: z.number(),
  relationsRemoved: z.number(),
  kept: z.number(),
  remaining: z.array(importRollbackBlockerSchema),
  ownersRemoved: z.number(),
});

export type ImportRollbackResult = AsContract<z.infer<typeof importRollbackResultSchema>>;

/**
 * ⚠ **Asercje zgodności z dokumentem OpenAPI.** Rozjazd kontraktu ma wychodzić przy
 * `pnpm typecheck`, a nie w przeglądarce (decyzja 74). Sprawdzenie idzie w OBIE strony:
 * pole dodane po stronie .NET-a i pole usunięte z naszego schematu dają ten sam błąd.
 */
export const contractAssertions = {
  ImportRunSummary: true satisfies MatchesSchema<ImportRunSummary, 'ImportRunSummary'>,
  ImportRunState: true satisfies MatchesSchema<ImportRunState, 'ImportRunStateDto'>,
  ImportConflictKind: true satisfies MatchesSchema<ImportConflictKind, 'ImportConflictKindDto'>,
  ImportConflictState: true satisfies MatchesSchema<ImportConflictState, 'ImportConflictStateDto'>,
  ImportCounts: true satisfies MatchesSchema<ImportCounts, 'ImportCounts'>,
  ImportConflictGroup: true satisfies MatchesSchema<ImportConflictGroup, 'ImportConflictGroup'>,
  ImportCampaignImpact: true satisfies MatchesSchema<ImportCampaignImpact, 'ImportCampaignImpact'>,
  ImportAnalysis: true satisfies MatchesSchema<ImportAnalysis, 'ImportAnalysis'>,
  ImportConflictItem: true satisfies MatchesSchema<ImportConflictItem, 'ImportConflictItem'>,
  ImportConflictDetail: true satisfies MatchesSchema<ImportConflictDetail, 'ImportConflictDetail'>,
  DuplicateSuspectPayload: true satisfies MatchesSchema<
    DuplicateSuspectPayload,
    'DuplicateSuspectPayload'
  >,
  ReparentPayload: true satisfies MatchesSchema<ReparentPayload, 'ReparentPayload'>,
  DeletedReturnsPayload: true satisfies MatchesSchema<
    DeletedReturnsPayload,
    'DeletedReturnsPayload'
  >,
  ValueDiffersPayload: true satisfies MatchesSchema<ValueDiffersPayload, 'ValueDiffersPayload'>,
  ImportFieldDiff: true satisfies MatchesSchema<ImportFieldDiff, 'ImportFieldDiff'>,
  UnknownValuePayload: true satisfies MatchesSchema<UnknownValuePayload, 'UnknownValuePayload'>,
  DanglingRefPayload: true satisfies MatchesSchema<DanglingRefPayload, 'DanglingRefPayload'>,
  ImportIntakeResult: true satisfies MatchesSchema<ImportIntakeResult, 'ImportIntakeResult'>,
  ImportResolveResult: true satisfies MatchesSchema<ImportResolveResult, 'ImportResolveResult'>,
  ImportChange: true satisfies MatchesSchema<ImportChange, 'ImportChange'>,
  ImportConflictPage: true satisfies MatchesSchema<
    ImportConflictPage,
    'ImportPageOfImportConflictItem'
  >,
  ImportChangePage: true satisfies MatchesSchema<ImportChangePage, 'ImportPageOfImportChange'>,
  ImportRollbackBlocker: true satisfies MatchesSchema<
    ImportRollbackBlocker,
    'ImportRollbackBlocker'
  >,
  ImportRollbackPreview: true satisfies MatchesSchema<
    ImportRollbackPreview,
    'ImportRollbackPreview'
  >,
  ImportRollbackResult: true satisfies MatchesSchema<ImportRollbackResult, 'ImportRollbackResult'>,
} as const;
